import { useEffect, useRef, useState } from "react";
import { Form, Input, Button, Typography, Alert } from "antd";
import { ILoginForm } from "common/interface";
import { mobileFormValidator } from "common/utils";
import { useNavigate } from "react-router-dom";
import { routes } from "common/routes";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { generateLoginOtp, loginWithOtp, resetLoginState } from "store/app/appSlice";

const { Link, Text } = Typography;

const RESEND_COUNTDOWN_SECONDS = 30;

const inputStyle: React.CSSProperties = {
  borderRadius: "10px",
  height: "60px",
};

const buttonStyle: React.CSSProperties = {
  borderRadius: "10px",
  height: "60px",
  marginTop: "30px",
};

interface LoginFormProps {
  onLogin: (user: ILoginForm) => void;
  loading: boolean;
}

export const LoginForm = ({ onLogin, loading = false }: LoginFormProps) => {
  const dispatch = useAppDispatch();
  const [mode, setMode] = useState<"password" | "otp">("otp");

  const switchMode = (next: "password" | "otp") => {
    dispatch(resetLoginState());
    setMode(next);
  };

  return mode === "password" ? (
    <PasswordLoginForm onLogin={onLogin} loading={loading} onSwitchToOtp={() => switchMode("otp")} />
  ) : (
    <OtpLoginForm onSwitchToPassword={() => switchMode("password")} />
  );
};

interface PasswordLoginFormProps extends LoginFormProps {
  onSwitchToOtp: () => void;
}

const PasswordLoginForm = ({ onLogin, loading = false, onSwitchToOtp }: PasswordLoginFormProps) => {
  const navigate = useNavigate();
  const { errors } = useAppSelector((state) => state.app);
  const onFinish = (values: ILoginForm) => {
    onLogin(values);
  };

  return (
    <Form
      name="normal_login"
      className="login-form"
      initialValues={{ remember: true }}
      onFinish={onFinish}
    >
      {!!errors?.length && (
        <Alert message={errors[0]} type="error" showIcon style={{ marginBottom: 16 }} />
      )}
      <Form.Item
        name="mobile"
        rules={[
          {
            required: true,
            message: "Please input your Mobile!",
          },
          {
            validator: function (rule, value) {
              return mobileFormValidator(value);
            },
            validateTrigger: "onFinish",
          },
        ]}
      >
        <Input
          // prefix={<>+91</>}
          placeholder="Mobile Number"
          size="large"
          maxLength={10}
          formNoValidate={false}
          style={{ borderRadius: "10px", height: "60px" }}
        />
      </Form.Item>
      <Form.Item
        name="password"
        rules={[{ required: true, message: "Please input your Password!" }]}
      >
        <Input
          type="password"
          placeholder="Enter Pin"
          size="large"
          style={{ borderRadius: "10px", height: "60px" }}
        />
      </Form.Item>

      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          block
          size="large"
          style={{ borderRadius: "10px", height: "60px", marginTop: "30px" }}
          loading={loading}
          disabled={loading}
        >
          Log in
        </Button>
      </Form.Item>
      <div style={{ textAlign: "center" }}>
        <Link onClick={onSwitchToOtp}>Login with OTP</Link>
      </div>
      <div style={{ textAlign: "center", marginTop: 8 }}>
        <Link onClick={() => navigate(`/${routes.FORGOT_PASSWORD}`)}>
          Forgot Password?
        </Link>
      </div>
    </Form>
  );
};

interface OtpLoginFormProps {
  onSwitchToPassword: () => void;
}

const OtpLoginForm = ({ onSwitchToPassword }: OtpLoginFormProps) => {
  const dispatch = useAppDispatch();
  const { status, errors, otpStatus, otpError } = useAppSelector((state) => state.app);

  const [step, setStep] = useState<"mobile" | "otp">("mobile");
  const [mobile, setMobile] = useState("");
  const [countdown, setCountdown] = useState(0);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [otpForm] = Form.useForm();

  const isLoading = status === "loading";
  const isSendingOtp = otpStatus === "loading";

  useEffect(() => {
    return () => {
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    };
  }, []);

  const startResendCountdown = () => {
    setCountdown(RESEND_COUNTDOWN_SECONDS);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    countdownTimerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownTimerRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleGetOtp = (values: { mobile: string }) => {
    setMobile(values.mobile);
    dispatch(generateLoginOtp(`+91${values.mobile}`)).then((result) => {
      if (generateLoginOtp.fulfilled.match(result)) {
        setStep("otp");
        startResendCountdown();
      }
    });
  };

  const handleResendOtp = () => {
    dispatch(generateLoginOtp(`+91${mobile}`)).then((result) => {
      if (generateLoginOtp.fulfilled.match(result)) {
        startResendCountdown();
        otpForm.resetFields();
      }
    });
  };

  const handleVerifyOtp = (values: { otp: string }) => {
    dispatch(loginWithOtp({ mobile: `+91${mobile}`, otp: values.otp }));
  };

  if (step === "mobile") {
    return (
      <Form name="otp_login_mobile" onFinish={handleGetOtp}>
        {otpStatus === "failed" && otpError && (
          <Alert message={otpError} type="error" showIcon style={{ marginBottom: 16 }} />
        )}
        <Form.Item
          name="mobile"
          rules={[
            { required: true, message: "Please input your Mobile!" },
            {
              validator: (_, value) => mobileFormValidator(value),
              validateTrigger: "onFinish",
            },
          ]}
        >
          <Input placeholder="Mobile Number" size="large" maxLength={10} style={inputStyle} />
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            block
            size="large"
            style={buttonStyle}
            loading={isSendingOtp}
            disabled={isSendingOtp}
          >
            Send OTP
          </Button>
        </Form.Item>
        <div style={{ textAlign: "center" }}>
          <Link onClick={onSwitchToPassword}>Login with Password</Link>
        </div>
      </Form>
    );
  }

  return (
    <Form form={otpForm} name="otp_login_verify" onFinish={handleVerifyOtp}>
      <Text type="secondary" style={{ marginBottom: 16, display: "block" }}>
        A 6-digit OTP has been sent to +91{mobile}
      </Text>
      {status === "failed" && !!errors?.length && (
        <Alert message={errors[0]} type="error" showIcon style={{ marginBottom: 16 }} />
      )}
      <Form.Item
        name="otp"
        rules={[
          { required: true, message: "Please enter the OTP!" },
          { pattern: /^\d{6}$/, message: "OTP must be exactly 6 digits!" },
        ]}
      >
        <Input
          placeholder="Enter 6-digit OTP"
          size="large"
          maxLength={6}
          style={{ ...inputStyle, letterSpacing: "12px", textAlign: "center" }}
        />
      </Form.Item>
      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          block
          size="large"
          style={buttonStyle}
          loading={isLoading}
          disabled={isLoading}
        >
          Verify OTP &amp; Log in
        </Button>
      </Form.Item>
      <div style={{ textAlign: "center" }}>
        {countdown > 0 ? (
          <Text type="secondary">Resend OTP in {countdown}s</Text>
        ) : (
          <Link onClick={isSendingOtp ? undefined : handleResendOtp}>Resend OTP</Link>
        )}
      </div>
      <div style={{ textAlign: "center", marginTop: 12 }}>
        <Link onClick={onSwitchToPassword}>Login with Password</Link>
      </div>
    </Form>
  );
};
